import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import pick from 'lodash/pick';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
export const closeDialog = (): void => window.connectHost?.dialog?.close();
export const getOpenDialog = (): HTMLIFrameElement | undefined | null => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const topEl = window.AJS?.LayerManager?.global?.getTopLayer();
	if (topEl == null) {
		return null;
	}
	return topEl.find('iframe')?.[0];
};

export const useModalDialogCleanupEffect = (): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	return useEffect(
		() => () => {
			try {
				const dialogBeforeClose = getOpenDialog();
				closeDialog();
				const dialogNow = getOpenDialog();

				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					dialogBeforeClose && dialogNow ? 'iframe not closed' : 'iframe closed',
					'apps',
					{
						// Match the unique key part separated by a final __ delimination
						uniqueKey: dialogBeforeClose?.id.match(/.*(?=__)/)?.[0] ?? dialogBeforeClose?.id,
						uniqueOpenKey: dialogNow?.id.match(/.*(?=__)/)?.[0] ?? dialogNow?.id,
					},
				);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'issue-navigator-action-pluggable-items.ui.useModalDialogCleanupEffect',
						packageName: 'jiraIssueNavigatorActionPluggableItems',
						teamName: 'empanada',
					},
				});
			}
		},
		[createAnalyticsEvent],
	);
};

export const useRegisterModalDialogAnalytics: () => () => void = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const iFrameEstablishedCallback = useCallback(
		(payload: { extension: { options: Record<string, unknown> } }) => {
			if (payload.extension.options.moduleType === 'dialogs') {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'iframe established',
					'apps',
					pick(payload.extension.options, [
						'addon_key',
						'isDialog',
						'dialogId',
						'isFullPage',
						'uniqueKey',
						'origin',
					]),
				);
			}
		},
		[createAnalyticsEvent],
	);
	const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>();
	const registerListeners = useCallback(() => {
		// Connect doesn't seem to have a way to notify when it has been registered, so we have to resort to polling
		timeoutIdRef.current = setTimeout(() => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			if (!window.connectHost) {
				registerListeners();
			} else {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.connectHost.onIframeEstablished(iFrameEstablishedCallback);
			}
		}, 1000);
	}, [iFrameEstablishedCallback]);

	useLayoutEffect(
		() => () => {
			clearTimeout(timeoutIdRef.current);
			// @ts-expect-error - TS2339 - Unsafe use of Connect's internal API
			if (iFrameEstablishedCallback._wrapper != null) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.connectHost?.offIframeEstablished(iFrameEstablishedCallback);
			}
			timeoutIdRef.current = undefined;
		},
		[iFrameEstablishedCallback, registerListeners],
	);

	return () => {
		if (timeoutIdRef.current === undefined) {
			registerListeners();
		}
	};
};

export const useInlineDialogZIndexOverrideLayoutEffect = (): void => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const { current: styleNode } = useRef<HTMLStyleElement>(document.createElement('style'));
	useLayoutEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.head?.appendChild(styleNode);
		styleNode.setAttribute('data-nin-pluggable-items', 'css');
		styleNode.sheet?.insertRule(
			'div.aui-inline-dialog[id^="inline-dialog-webitem-"]{z-index:401}',
			0,
		);
		return () => {
			styleNode.remove();
		};
	}, [styleNode]);
};
